import { useEffect, useMemo, useRef } from 'react';
import { formatPrice } from '../../utils/futuresProduct';
import { auditBars, auditRenderContext, isChartDebugEnabled } from '@formosa/shared/utils/chartDebug';

const UP_COLOR = '#22c55e';
const DOWN_COLOR = '#ef4444';
const AXIS_COLOR = '#9ca3af';
const MAX_VISIBLE_BARS = 80;

function readBarPrices(bar) {
  return {
    open: Number(bar?.open),
    high: Number(bar?.high),
    low: Number(bar?.low),
    close: Number(bar?.close),
  };
}

function isValidBar(bar) {
  const prices = readBarPrices(bar);
  return [prices.open, prices.high, prices.low, prices.close].every((v) => !Number.isNaN(v));
}

export default function FuturesCandlestickChart({ bars, currentPrice, height = 200, loading = false }) {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);

  const visibleBars = useMemo(() => {
    if (!Array.isArray(bars)) {
      return [];
    }
    return bars.filter(isValidBar).slice(-MAX_VISIBLE_BARS);
  }, [bars]);

  useEffect(() => {
    if (!isChartDebugEnabled()) {
      return;
    }
    console.info('[ChartDebug] 實際渲染元件: FuturesCandlestickChart (Canvas 2D candlestick)');
    console.info('[ChartDebug] 非 CSS 佔位條 (.futures-trade-page__bar 已移除)');
    auditBars(bars, 'chart data (props.bars)', { currentPrice });
    auditBars(visibleBars, 'chart data (visibleBars)', { maxVisible: MAX_VISIBLE_BARS, currentPrice });
    auditRenderContext(visibleBars, currentPrice, {
      filteredOutCount: Array.isArray(bars) ? bars.length - visibleBars.length : 0,
    });
  }, [bars, visibleBars, currentPrice]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) {
      return undefined;
    }

    const draw = () => {
      const dpr = window.devicePixelRatio || 1;
      const width = container.clientWidth;
      const chartHeight = height;
      canvas.width = Math.max(1, width * dpr);
      canvas.height = Math.max(1, chartHeight * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${chartHeight}px`;

      const ctx = canvas.getContext('2d');
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, width, chartHeight);

      if (visibleBars.length === 0) {
        ctx.fillStyle = '#6b7280';
        ctx.font = '12px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(loading ? 'K 線載入中…' : '暫無 K 線資料', width / 2, chartHeight / 2);
        return;
      }

      const priceAxisWidth = 76;
      const chartPadding = { top: 14, bottom: 14, left: 6, right: 6 };
      const chartWidth = Math.max(40, width - priceAxisWidth - chartPadding.left);
      const plotHeight = chartHeight - chartPadding.top - chartPadding.bottom;

      let minPrice = Infinity;
      let maxPrice = -Infinity;
      let highValue = -Infinity;
      let lowValue = Infinity;
      let highIndex = 0;
      let lowIndex = 0;

      visibleBars.forEach((bar, index) => {
        const prices = readBarPrices(bar);
        minPrice = Math.min(minPrice, prices.low);
        maxPrice = Math.max(maxPrice, prices.high);
        if (prices.high >= highValue) {
          highValue = prices.high;
          highIndex = index;
        }
        if (prices.low <= lowValue) {
          lowValue = prices.low;
          lowIndex = index;
        }
      });

      const livePrice = Number(currentPrice);
      if (!Number.isNaN(livePrice)) {
        minPrice = Math.min(minPrice, livePrice);
        maxPrice = Math.max(maxPrice, livePrice);
      }

      const spread = maxPrice - minPrice;
      const padding = spread > 0 ? spread * 0.06 : Math.max(maxPrice, 1) * 0.001;
      minPrice -= padding;
      maxPrice += padding;
      const priceRange = maxPrice - minPrice || 1;

      const yFor = (price) =>
        chartPadding.top + plotHeight - ((price - minPrice) / priceRange) * plotHeight;

      const slotWidth = chartWidth / visibleBars.length;
      const bodyWidth = Math.max(2, Math.min(8, slotWidth * 0.62));

      visibleBars.forEach((bar, index) => {
        const prices = readBarPrices(bar);
        const x = chartPadding.left + index * slotWidth + slotWidth / 2;
        const up = prices.close >= prices.open;
        const color = up ? UP_COLOR : DOWN_COLOR;
        const bodyTop = yFor(Math.max(prices.open, prices.close));
        const bodyBottom = yFor(Math.min(prices.open, prices.close));

        ctx.strokeStyle = color;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(x, yFor(prices.high));
        ctx.lineTo(x, yFor(prices.low));
        ctx.stroke();

        ctx.fillStyle = color;
        ctx.fillRect(x - bodyWidth / 2, bodyTop, bodyWidth, Math.max(1, bodyBottom - bodyTop));
      });

      if (!Number.isNaN(livePrice)) {
        const y = yFor(livePrice);
        ctx.strokeStyle = DOWN_COLOR;
        ctx.lineWidth = 1;
        ctx.setLineDash([4, 4]);
        ctx.beginPath();
        ctx.moveTo(chartPadding.left, y);
        ctx.lineTo(chartPadding.left + chartWidth, y);
        ctx.stroke();
        ctx.setLineDash([]);

        ctx.fillStyle = DOWN_COLOR;
        ctx.fillRect(width - priceAxisWidth, y - 9, priceAxisWidth, 18);
        ctx.fillStyle = '#fff';
        ctx.font = '10px sans-serif';
        ctx.textAlign = 'right';
        ctx.fillText(formatPrice(livePrice, 4), width - 4, y + 4);
      }

      ctx.fillStyle = AXIS_COLOR;
      ctx.font = '10px sans-serif';
      ctx.textAlign = 'right';
      ctx.fillText(formatPrice(maxPrice, 4), width - 4, chartPadding.top + 10);
      ctx.fillText(formatPrice(minPrice, 4), width - 4, chartPadding.top + plotHeight);

      const drawExtremeLabel = (index, value, above) => {
        const x = chartPadding.left + index * slotWidth + slotWidth / 2;
        const y = yFor(value) + (above ? -6 : 12);
        ctx.fillStyle = AXIS_COLOR;
        ctx.font = '9px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(formatPrice(value, 6), x, y);
      };

      drawExtremeLabel(highIndex, highValue, true);
      drawExtremeLabel(lowIndex, lowValue, false);
    };

    draw();
    const observer = new ResizeObserver(draw);
    observer.observe(container);
    return () => observer.disconnect();
  }, [visibleBars, currentPrice, height, loading]);

  return (
    <div ref={containerRef} className="futures-candlestick-chart">
      <canvas ref={canvasRef} role="img" aria-label="K 線圖" />
    </div>
  );
}
